<template>
  <div class="col-12 col-xl-12" v-can="'grade_sheets.display'">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <button class="btn btn-primary btn-sm print-hide" @click="printGradeSheet">
        <i class="fas fa-print ms-2"></i>
        {{ t("gradeSheets.print") }}
      </button>

      <button
        v-if="!readOnly"
        @click="navigateToEdit(gradeSheet.id)"
        class="btn btn-sm btn-secondary me-2"
        v-can="'grade_sheets.update'"
      >
        <i class="fas fa-edit me-1"></i> {{ t("gradeSheets.edit") }}
      </button>
    </div>

    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ gradeSheet.title }}</h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loading"
            :message="t('gradeSheets.loading')"
            class="py-5"
          />

          <template v-else>
            <div class="d-flex justify-content-between mb-4 mx-3 print-header">
              <div>
                <h6 class="mb-1">
                  {{ t("gradeSheets.class") }}:
                  <span :class="{ 'text-muted': !gradeSheet.class }">
                    {{ gradeSheet.class?.name || t("gradeSheets.loading") }}
                  </span>
                </h6>
                <h6 class="mb-1">
                  {{ t("gradeSheets.subject") }}:
                  <span :class="{ 'text-muted': !gradeSheet.subject }">
                    {{ gradeSheet.subject?.name || t("gradeSheets.loading") }}
                  </span>
                </h6>
              </div>
              <div class="text-end">
                <h6 class="mb-1">
                  {{ t("gradeSheets.academic_year") }}:
                  <span :class="{ 'text-muted': !gradeSheet.academic_year }">
                    {{ gradeSheet.academic_year?.name || gradeSheet.academic_year?.year || t("gradeSheets.loading") }}
                    <span v-if="gradeSheet.academic_year?.is_active" class="badge bg-success ms-1">
                      {{ t('common.current') }}
                    </span>
                  </span>
                </h6>
                <h6 class="mb-1">
                  {{ t("gradeSheets.createdAt") }}:
                  <span :class="{ 'text-muted': !gradeSheet.created_at }">
                    {{ gradeSheet.created_at ? formatDate(gradeSheet.created_at) : t("gradeSheets.loading") }}
                  </span>
                </h6>
              </div>
            </div>

            <div class="table-responsive" id="gradeSheetTable">
              <table class="table align-items-center mb-0">
                <thead v-if="gradeSheet.structures && gradeSheet.structures.length">
                  <tr>
                    <th>{{ t("gradeSheets.studentName") }}</th>
                    <th
                      v-for="structure in gradeSheet.structures"
                      :key="structure.id"
                      class="text-center"
                      :style="{ backgroundColor: structure.column_color, color: getContrastColor(structure.column_color) }"
                    >
                      {{ structure.column_name }} ({{ structure.full_degree }})
                    </th>
                    <th class="text-center font-weight-bolder">{{ t("gradeSheets.total") }}</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-for="student in students" :key="student.id">
                    <td>{{ student?.first_name }} {{ student?.father_name }} {{ student?.last_name }}</td>

                    <td
                      v-for="structure in gradeSheet.structures"
                      :key="structure.id"
                      class="text-center"
                    >
                      <template v-if="readOnly">
                        {{ getStudentDegree(student.id, structure.id) }}
                      </template>
                      <template v-else>
                        <input
                          type="number"
                          class="form-control form-control-sm text-center print-input"
                          :value="getStudentDegree(student.id, structure.id)"
                          @input="handleInputChange(student.id, structure.id, $event)"
                          @change="updateDegree(student.id, structure.id, $event)"
                          :max="structure.full_degree"
                          step="0.01"
                          min="0"
                        />
                      </template>
                    </td>

                    <td class="text-center font-weight-bold">
                      {{ studentTotals[student.id] || calculateStudentTotal(student.id) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import api from '@/api';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const toast = useToast();
const router = useRouter();

const readOnly = computed(() => String(route.query.readonly ?? '') === '1');

const loading = ref(true);
const gradeSheet = ref({
  title: '',
  class: null,
  subject: null,
  academic_year: null,
  structures: [],
  created_at: ''
});
const students = ref([]);
const studentTotals = reactive({});

const fetchData = async () => {
  try {
    loading.value = true;
    
    // Fetch grade sheet with academic year
    const gradeSheetResponse = await store.dispatch('gradeSheets/fetchGradeSheet', route.params.id);
    
    console.log('Grade sheet response:', gradeSheetResponse); // Debug log
    
    gradeSheet.value = {
      title: gradeSheetResponse?.title || '',
      class: gradeSheetResponse?.class || null,
      subject: gradeSheetResponse?.subject || null,
      academic_year: gradeSheetResponse?.academic_year || null,
      structures: gradeSheetResponse?.structures || [],
      created_at: gradeSheetResponse?.created_at || '',
      id: gradeSheetResponse?.id || null,
      class_group_id: gradeSheetResponse?.class_group_id || null,
      group_id: gradeSheetResponse?.group_id ?? gradeSheetResponse?.group?.id ?? null,
    };

    // Debug: Check if academic_year is loaded
    if (!gradeSheet.value.academic_year && gradeSheetResponse?.academic_year_id) {
      console.log('Academic year ID exists but not loaded:', gradeSheetResponse.academic_year_id);
      // Try to fetch academic year separately
      try {
        const academicYearRes = await api.get(`/academic-years/${gradeSheetResponse.academic_year_id}`);
        gradeSheet.value.academic_year = academicYearRes.data.data;
      } catch (error) {
        console.error('Error fetching academic year:', error);
      }
    }

    const classId = gradeSheet.value.class?.id;
    const groupId = gradeSheet.value.group_id;

    if (classId && groupId) {
      await store.dispatch('groupstudent/fetchGroupStudents', { classId, groupId });
      students.value = store.state.groupstudent.students;
    } else if (gradeSheet.value.class_group_id) {
      const res = await api.get(`/class-groups/${gradeSheet.value.class_group_id}/students`);
      students.value = res.data?.data ?? res.data ?? [];
    } else if (classId) {
      const res = await api.get(`/classes/${classId}/students`);
      students.value = res.data?.data ?? [];
    } else {
      students.value = [];
      toast.warning(t('gradeSheets.noClassLinked'));
    }
  } catch (error) {
    toast.error(t('gradeSheets.failedLoadStudents'));
    console.error('Error fetching data:', error);
    students.value = [];
  } finally {
    loading.value = false;
  }
};

const getStudentDegree = (studentId, structureId) => {
  const structure = gradeSheet.value.structures.find(s => s.id === structureId);
  if (!structure) return '';
  const content = structure.contents?.find(c => c.student?.id === studentId);
  return content ? content.degree : '';
};

const calculateStudentTotal = (studentId) => {
  let total = 0;
  gradeSheet.value.structures.forEach(structure => {
    const content = structure.contents?.find(c => c.student?.id === studentId);
    if (content) total += parseFloat(content.degree) || 0;
  });
  return total.toFixed(2);
};

const navigateToEdit = (id) => router.push(`/grade-sheets/${id}/edit`);

const handleInputChange = (studentId, structureId, event) => {
  if (readOnly.value) return;
  const newDegree = event.target.value;
  const structure = gradeSheet.value.structures.find(s => s.id === structureId);
  if (!structure.contents) structure.contents = [];
  let content = structure.contents.find(c => c.student?.id === studentId);

  if (content) {
    content.degree = newDegree;
  } else {
    structure.contents.push({ student: { id: studentId }, degree: newDegree });
  }

  studentTotals[studentId] = calculateStudentTotal(studentId);
};

const updateDegree = async (studentId, structureId, event) => {
  if (readOnly.value) return;
  const newDegree = event.target.value;

  try {
    await store.dispatch('gradeSheets/updateGradeContent', {
      structureId,
      contentData: {
        student_id: studentId,
        degree: newDegree,
        ...(store.state.user?.role?.slug !== 'teacher' && store.state.selectedTeacherId
          ? { teacher_id: store.state.selectedTeacherId }
          : {})
      }
    });
    toast.success(t('gradeSheets.degreeUpdated'));
  } catch (error) {
    toast.error(t('gradeSheets.failedUpdateDegree'));
    console.error('Update error:', error);
    event.target.value = getStudentDegree(studentId, structureId);
    studentTotals[studentId] = calculateStudentTotal(studentId);
  }
};

const printGradeSheet = () => {
  const printWindow = window.open('', '_blank');
  const printContent = `
    <!DOCTYPE html>
    <html dir="rtl">
    <head>
      <meta charset="UTF-8">
      <title>${gradeSheet.value.title}</title>
      <style>
        @page { size: A4; margin: 20mm; }
        body { font-family: 'Arial', sans-serif; line-height: 1.6; color: #333; padding: 20px; }
        .print-container { max-width: 100%; margin: 0 auto; }
        .print-header { margin-bottom: 20px; padding-bottom: 10px; border-bottom: 2px solid #eee; }
        .print-title { text-align: center; margin-bottom: 20px; color: #2c3e50; font-size: 24px; font-weight: bold; }
        .print-meta { display: flex; justify-content: space-between; margin-bottom: 15px; }
        .print-table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        .print-table th, .print-table td { border: 1px solid #dee2e6; padding: 8px; text-align: center; }
        .print-table tr:nth-child(even) { background-color: #f9f9f9; }
        .print-input { border: none; background: transparent; width: 100%; text-align: center; }
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="print-title">${gradeSheet.value.title}</div>
        <div class="print-meta">
          <div>
            <strong>${t('gradeSheets.class')}:</strong> ${gradeSheet.value?.class?.name || ''}<br>
            <strong>${t('gradeSheets.subject')}:</strong> ${gradeSheet.value?.subject?.name || ''}<br>
            <strong>${t('gradeSheets.academic_year')}:</strong> ${gradeSheet.value?.academic_year?.name || gradeSheet.value?.academic_year?.year || ''}
          </div>
          <div>
            <strong>${t('gradeSheets.createdAt')}:</strong> ${gradeSheet.value?.created_at ? formatDate(gradeSheet.value.created_at) : ''}<br>
            <strong>${t('gradeSheets.semester')}:</strong> الأول 2023/2024
          </div>
        </div>
        <table class="print-table">
          <thead>
            <tr>
              <th>${t('gradeSheets.studentName')}</th>
              ${gradeSheet.value.structures.map(structure => `
                <th style="background-color: ${structure.column_color}; color: ${getContrastColor(structure.column_color)}">
                  ${structure.column_name} (${structure.full_degree})
                </th>
              `).join('')}
              <th>${t('gradeSheets.total')}</th>
            </tr>
          </thead>
          <tbody>
            ${students.value.map(student => `
              <tr>
                <td>${student.first_name} ${student.father_name} ${student.last_name}</td>
                ${gradeSheet.value.structures.map(structure => `<td>${getStudentDegree(student.id, structure.id)}</td>`).join('')}
                <td><strong>${studentTotals[student.id] || calculateStudentTotal(student.id)}</strong></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div style="margin-top: 30px; display: flex; justify-content: space-between;">
          <div style="width: 200px; border-top: 1px solid #333; text-align: center; padding-top: 5px;">
            ${t('gradeSheets.teacherSignature')}
          </div>
          <div style="width: 200px; border-top: 1px solid #333; text-align: center; padding-top: 5px;">
            ${t('gradeSheets.managerSignature')}
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
  printWindow.document.open();
  printWindow.document.write(printContent);
  printWindow.document.close();
  printWindow.onload = () => setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('ar-LY', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

const getContrastColor = (hexColor) => {
  if (!hexColor) return '#000';
  const r = parseInt(hexColor.substr(1,2),16);
  const g = parseInt(hexColor.substr(3,2),16);
  const b = parseInt(hexColor.substr(5,2),16);
  const luminance = (0.299*r + 0.587*g + 0.114*b)/255;
  return luminance > 0.5 ? '#000' : '#fff';
};

onMounted(fetchData);

watch(
  () => store.getters['gradeSheets/getCurrentGradeSheet'],
  (newGradeSheet) => {
    if (newGradeSheet && newGradeSheet.id === parseInt(route.params.id)) {
      gradeSheet.value = {
        ...gradeSheet.value,
        ...newGradeSheet,
        academic_year: newGradeSheet.academic_year || gradeSheet.value.academic_year
      };
    }
  }
);
</script>